/*
    Both the app and portal display statements + documents in a single list (overload the functions to handle both cases)
    This utility combines -> sorts -> outputs the display data necessary for the components
*/

import { DateTime } from 'luxon';

import {
  AppPaymentDueFragment,
  BorrowerDocumentAccess as AppBorrowerAccess,
  DocumentCategory,
  DocumentType,
  LoanDocumentDeliveryStatus,
} from '@willow/graphql-iso/src/app';
import { PortalPaymentDueFragment } from '@willow/graphql-iso/src/portal';
import { DocumentId, PaymentDueId } from '@willow/types-iso';

import { getDisplayablePaymentsDue } from './currentPaymentDue';
import { dateFormat } from './dateFormat';
import { formatWorkoutPlanType } from './formatWorkoutPlan';

export const RevisedCallout = 'Replaced by revised copy';

/* DEFINE TYPES FOR TABLE DISPLAY FORMATTING */
type BaseItem = {
  notificationType: 'document' | 'email' | 'manuallyUploaded';
  category?: DocumentCategory; // optional: the portal does not display categories
  title: string;
  subtitle?: string;
  callouts: string[];
  sortByDate: Date;
  version: number;
  date: Date | string; // date pre-sorted -> string formatted post-sorting
  borrowerAccess?: AppBorrowerAccess;
  isArchived: boolean;
};

export type StatementItem = BaseItem & {
  id: PaymentDueId;
  documentType: 'Statement';
};

export type DocumentItem = BaseItem & {
  id: DocumentId;
  documentType: 'Document';
  documentSpecificationType: DocumentType;
  delivery?: { status: LoanDocumentDeliveryStatus; date?: string };
};

export type DocumentDisplayItem = StatementItem | DocumentItem;

/* HELPER FUNCTIONS FOR FORMATTING TABLE ITEMS (APP + PORTAL OVERLOADED */
export function getStatementItems(
  paymentsDue: AppPaymentDueFragment[],
  transferDate: Date | undefined,
): DocumentDisplayItem[];
export function getStatementItems(
  paymentsDue: PortalPaymentDueFragment[],
  transferDate: Date | undefined,
): DocumentDisplayItem[];
export function getStatementItems(
  paymentsDue: AppPaymentDueFragment[] | PortalPaymentDueFragment[],
  transferDate: Date | undefined,
): DocumentDisplayItem[] {
  // Filter out statements that we don't expect to collect on
  const collectablePayments = getDisplayablePaymentsDue(paymentsDue, transferDate);

  const mappedStatements = collectablePayments.flatMap((paymentDue) => {
    const { id, statementDate, paymentDate, status } = paymentDue;
    const statements = paymentDue.statements.map((s, index) => {
      return { ...s, isOutdated: index < paymentDue.statements.length - 1 };
    });

    // Flatten statements (there may be multiple per paymentDue if there are revisions)
    return statements?.flat().map((statement) => {
      const callouts: string[] = [];
      const { createdAt, version, isOutdated } = statement;
      isOutdated && callouts.push(RevisedCallout);

      if (status.workoutPlanType) {
        callouts.push(formatWorkoutPlanType(status.workoutPlanType));
      }
      const statementSentDate = getStatementSentDate(createdAt, statementDate);

      return {
        id,
        notificationType: 'document' as const,
        category: DocumentCategory.Statement,
        documentType: 'Statement' as const,
        title: `Statement (${dateFormat(paymentDate, 'LLL yyyy')})`,
        callouts,
        sortByDate: new Date(statementDate),
        date: statementSentDate,
        version,
        borrowerAccess: 'borrowerAccess' in statement ? statement.borrowerAccess : undefined, // Shared component, but portal does not have borrowerAccess
        isArchived: isOutdated,
      };
    });
  });

  return mappedStatements;
}

const getStatementSentDate = (createdAt: any, statementDate: any) => {
  createdAt = new Date(createdAt);
  statementDate = new Date(statementDate);

  return DateTime.max(DateTime.fromJSDate(createdAt), DateTime.fromJSDate(statementDate)).toJSDate();
};

export const sortDocumentsByDate = (documents: DocumentDisplayItem[]): DocumentDisplayItem[] => {
  // Sorted by createdAt date (newest documents display first)
  const sortedByDate = documents.sort((a, b) => {
    const dateA = new Date(a.sortByDate).getTime();
    const dateB = new Date(b.sortByDate).getTime();

    if (dateA === dateB) {
      return b.version > a.version ? 1 : -1;
    }

    // Return most recent date first
    return dateB > dateA ? 1 : -1;
  });

  return sortedByDate;
};
